import { config } from "@/config/env";
import type {
  AccommodationDayInfoResponse,
  AccommodationTypeAttributes,
  ApiErrorResponse,
  AvailabilityResponse,
  BookingDetails,
  CampgroundDataResponse,
  CheckoutResponse,
  PriceCheckResponse,
  PricesResponse,
  StripePaymentIntentResponse,
  StripePaymentStatusResponse,
} from "@/types/api";
import type { CheckoutData, SelectedPerson } from "@/types/booking";

interface ApiConfig {
  language: string;
  campground: string;
  token: string | null;
  aid: string | null;
}

let apiConfig: ApiConfig = {
  language: "en",
  campground: "",
  token: null,
  aid: null,
};

function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (apiConfig.aid) {
    headers["CAMPNERD-CHANGER-AID"] = apiConfig.aid;
  }

  return headers;
}

function addTokenToUrl(url: string): string {
  if (!apiConfig.token) return url;

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}token=${encodeURIComponent(apiConfig.token)}`;
}

const REQUEST_TIMEOUT_MS = 10_000;

function isDemoMode(): boolean {
  try {
    const stored = localStorage.getItem("campnerd_feature_flags");
    if (stored) {
      const flags = JSON.parse(stored) as { demoMode?: boolean };
      if (typeof flags.demoMode === "boolean") {
        return flags.demoMode;
      }
    }
  } catch {
    // ignore
  }
  return config.demoMode;
}

async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = addTokenToUrl(endpoint);
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS);

  const response = await fetch(url, {
    ...options,
    signal: options.signal ? AbortSignal.any([options.signal, timeout]) : timeout,
    headers: {
      ...getHeaders(),
      ...options.headers,
    },
  });

  let responseBody: unknown = null;
  const contentType = response.headers.get("content-type");

  if (contentType?.includes("application/json")) {
    responseBody = await response.json();
  } else {
    responseBody = await response.text();
  }

  if (!response.ok) {
    const message =
      typeof responseBody === "object" && responseBody !== null
        ? ((responseBody as { message: string }).message ?? response.statusText)
        : response.statusText;

    throw new ApiError(message, response.status, responseBody);
  }

  if (
    typeof responseBody === "object" &&
    responseBody !== null &&
    (responseBody as ApiErrorResponse).error === true
  ) {
    const { message } = responseBody as ApiErrorResponse;
    throw new ApiError(message, response.status, responseBody);
  }

  return responseBody as T;
}

export class ApiError<T = unknown> extends Error {
  status: number;
  data?: T;

  constructor(message: string, status: number, data?: T) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export const api = {
  configure(config: Partial<ApiConfig>) {
    apiConfig = { ...apiConfig, ...config };
  },

  setLanguage(language: string) {
    apiConfig.language = language;
  },

  setCampground(campground: string) {
    apiConfig.campground = campground;
  },

  setToken(token: string | null) {
    apiConfig.token = token;
  },

  setAid(aid: string | null) {
    apiConfig.aid = aid;
  },

  async getCampgroundData(
    language?: string,
    params: Record<string, string> = {},
  ): Promise<CampgroundDataResponse> {
    const queryString = new URLSearchParams(params).toString();
    const url = `${config.apiEndpoint}/${language}/api/checkout-data/${
      apiConfig.campground
    }/${queryString ? `?${queryString}` : ""}`;

    return fetchApi<CampgroundDataResponse>(url);
  },

  async getAccommodationAttributes(language: string): Promise<AccommodationTypeAttributes[]> {
    const url = `${config.apiEndpoint}/${language}/api/accommodation-typ-attributes-entries/${apiConfig.campground}/`;
    return fetchApi<AccommodationTypeAttributes[]>(url);
  },

  async checkAvailability(
    url: string,
    params: {
      date_from: string;
      date_until: string;
      currency_alt?: string;
      booking_id?: string;
    },
    persons: SelectedPerson[],
  ): Promise<AvailabilityResponse> {
    const queryString = new URLSearchParams({
      date_from: params.date_from,
      date_until: params.date_until,
      get_priceinfo: "1",
      ...(params.currency_alt && { currency_alt: params.currency_alt }),
      ...(params.booking_id && { booking_id: params.booking_id }),
    }).toString();
    return fetchApi<AvailabilityResponse>(`${url}?${queryString}`, {
      method: "POST",
      body: JSON.stringify({ persons }),
    });
  },

  async checkPrice(
    url: string,
    params: {
      currency_alt?: string;
      booking_id?: string;
    },
    bookingData: {
      date_from: string;
      date_until: string;
      campsites: Array<{
        id: number;
        amount: number;
        persons: SelectedPerson[];
      }>;
      extras: Array<{ id: number; amount: number }>;
    },
  ): Promise<PriceCheckResponse> {
    const queryString = new URLSearchParams({
      get_priceinfo: "1",
      ...(params.currency_alt && { currency_alt: params.currency_alt }),
      ...(params.booking_id && { booking_id: params.booking_id }),
    }).toString();

    return fetchApi<PriceCheckResponse>(`${url}?${queryString}`, {
      method: "POST",
      body: JSON.stringify({ booking_data: bookingData }),
    });
  },

  async getPrices(
    url: string,
    params: {
      date_from: string;
      date_until: string;
      currency_alt?: string;
      persons?: number;
      pets?: number;
      acco_id?: number;
    },
  ): Promise<PricesResponse> {
    const queryString = new URLSearchParams(
      Object.entries(params).reduce(
        (acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = String(value);
          }
          return acc;
        },
        {} as Record<string, string>,
      ),
    ).toString();

    return fetchApi<PricesResponse>(`${url}?${queryString}`);
  },

  async submitCheckout(url: string, checkoutData: CheckoutData): Promise<CheckoutResponse> {
    if (isDemoMode()) {
      return { booking_id: "demo-000001", booking_number: "DEMO-000001" };
    }
    // Relative URL, use fetchApi (goes through proxy in production)
    return fetchApi<CheckoutResponse>(url, {
      method: "POST",
      body: JSON.stringify(checkoutData),
    });
  },

  async getBookingDetails(
    url: string,
    campgroundSlug: string,
    bookingId: string,
  ): Promise<BookingDetails> {
    if (isDemoMode()) {
      const stored = sessionStorage.getItem("campnerd_demo_booking_details");
      if (stored) {
        return JSON.parse(stored) as BookingDetails;
      }
      throw new ApiError("Demo booking details not found in session", 404);
    }
    const endpoint = `${url}${campgroundSlug}/${bookingId}/`;
    return fetchApi<BookingDetails>(endpoint);
  },

  async prepareBooking(url: string, bookingId: string): Promise<{ success: boolean }> {
    if (isDemoMode()) {
      return { success: true };
    }
    const endpoint = `${url}?booking_id=${bookingId}`;
    return fetchApi<{ success: boolean }>(endpoint);
  },

  async cancelPendingBooking(url: string, bookingId: string): Promise<{ success: boolean }> {
    if (isDemoMode()) {
      return { success: true };
    }
    const endpoint = `${url}?booking_id=${bookingId}`;
    return fetchApi<{ success: boolean }>(endpoint);
  },

  async processPayload(
    url: string,
    data: {
      payment: string;
      payload: unknown;
      testmode: boolean;
    },
  ): Promise<{ success: boolean }> {
    return fetchApi<{ success: boolean }>(url, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  async createStripePaymentIntent(
    amount: number,
    bookingId: string,
  ): Promise<StripePaymentIntentResponse> {
    const url = `${config.apiEndpoint}/${apiConfig.language}/api/payments/stripe/create-payment-intent/`;
    return fetchApi<StripePaymentIntentResponse>(url, {
      method: "POST",
      body: JSON.stringify({ amount, bookingId }),
    });
  },

  async getStripePaymentStatus(
    bookingId: string,
    paymentIntent: string,
  ): Promise<StripePaymentStatusResponse> {
    const url = `${config.apiEndpoint}/${apiConfig.language}/api/payments/stripe/${bookingId}/get-payment-status/${paymentIntent}`;
    return fetchApi<StripePaymentStatusResponse>(url);
  },

  async getAccommodationDayInfo(params: {
    dateFrom: string;
    dateTo: string;
    accoId: number;
  }): Promise<AccommodationDayInfoResponse> {
    const queryString = new URLSearchParams({
      date_from: params.dateFrom,
      date_to: params.dateTo,
      acco_id: String(params.accoId),
    }).toString();
    const url = `${config.apiEndpoint}/${apiConfig.language}/api/accommodation-day-info/${apiConfig.campground}/?${queryString}`;
    return fetchApi<AccommodationDayInfoResponse>(url);
  },
};
